import React, { useState, useMemo } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  Collapse,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  Add,
  Edit,
  Delete,
  Close,
  Article,
  KeyboardArrowDown,
  KeyboardArrowUp,
  InfoOutlined,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListAboutUsQuery,
  useAddAboutUsMutation,
  useUpdateAboutUsMutation,
  useDeleteAboutUsMutation,
  AboutUs,
} from '../../features/admin/aboutApi.ts';

// ─── Form State ───────────────────────────────────────────────────────────────

interface FormData {
  heading: string;
  para1: string;
  para2: string;
  para3: string;
  is_active: number;
}

interface FormErrors {
  heading?: string;
}

const defaultForm: FormData = {
  heading:   '',
  para1:     '',
  para2:     '',
  para3:     '',
  is_active: 1,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-AE', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return dateStr; }
};

const truncate = (str: string | null | undefined, n = 80) => {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const AboutUsPage = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [page,          setPage]          = useState(1);
  const [searchStr,     setSearchStr]     = useState('');
  const [tabValue,      setTabValue]      = useState(0);
  const [expandedId,    setExpandedId]    = useState<number | null>(null);
  const [snackbar,      setSnackbar]      = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Dialog
  const [formOpen,      setFormOpen]      = useState(false);
  const [editingItem,   setEditingItem]   = useState<AboutUs | null>(null);
  const [formData,      setFormData]      = useState<FormData>(defaultForm);
  const [formErrors,    setFormErrors]    = useState<FormErrors>({});
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [deletingId,    setDeletingId]    = useState<number | null>(null);

  // API hooks
  const { data: listResponse, isLoading, isError, refetch } = useListAboutUsQuery();
  const [addAboutUs]    = useAddAboutUsMutation();
  const [updateAboutUs] = useUpdateAboutUsMutation();
  const [deleteAboutUs, { isLoading: isDeleting }] = useDeleteAboutUsMutation();

  const allItems: AboutUs[] = listResponse?.data || [];
  const usersPerPage = 10;

  // ── Filtered + paginated ──────────────────────────────────────────────────
  const filteredItems = useMemo(() =>
    allItems.filter((item) => {
      if (!searchStr) return true;
      const sl = searchStr.toLowerCase();
      return (
        item.heading?.toLowerCase().includes(sl) ||
        item.para1?.toLowerCase().includes(sl)   ||
        item.para2?.toLowerCase().includes(sl)   ||
        item.para3?.toLowerCase().includes(sl)
      );
    }),
  [allItems, searchStr]);

  const totalPages    = Math.ceil(filteredItems.length / usersPerPage);
  const paginatedList = filteredItems.slice((page - 1) * usersPerPage, page * usersPerPage);

  // Stats
  const activeCount   = allItems.filter(i => Number(i.is_active) === 1).length;
  const inactiveCount = allItems.filter(i => Number(i.is_active) === 0).length;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleDrawerToggle  = () => setMobileOpen(v => !v);
  const handleChangePage    = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleSearch        = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchStr(e.target.value); setPage(1); };
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const clearFilters        = () => { setSearchStr(''); setPage(1); };
  const handleToggleExpand  = (id: number) => setExpandedId(v => v === id ? null : id);

  const handleRefresh = async () => {
    try { await refetch(); setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' }); }
    catch { setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' }); }
  };

  // ── Form helpers ──────────────────────────────────────────────────────────
  const resetForm   = () => { setFormData(defaultForm); setFormErrors({}); setEditingItem(null); };
  const handleAdd   = () => { resetForm(); setFormOpen(true); };
  const handleClose = () => { setFormOpen(false); resetForm(); };

  const handleEdit = (item: AboutUs) => {
    setFormData({
      heading:   item.heading   || '',
      para1:     item.para1     || '',
      para2:     item.para2     || '',
      para3:     item.para3     || '',
      is_active: Number(item.is_active) ?? 1,
    });
    setEditingItem(item);
    setFormOpen(true);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormErrors]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const err: FormErrors = {};
    if (!formData.heading.trim()) err.heading = 'Heading is required';
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        heading:   formData.heading.trim(),
        para1:     formData.para1.trim()  || undefined,
        para2:     formData.para2.trim()  || undefined,
        para3:     formData.para3.trim()  || undefined,
        is_active: formData.is_active,
      };
      const result = await (editingItem
        ? updateAboutUs({ id: editingItem.id, ...payload })
        : addAboutUs(payload)
      ).unwrap();

      setSnackbar({ open: true, message: result.message || `${editingItem ? 'Updated' : 'Added'} successfully!`, severity: 'success' });
      handleClose();
      refetch();
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.data?.message || error?.message || 'Error saving record.', severity: 'error' });
    } finally { setIsSubmitting(false); }
  };

  const handleDelete = async (item: AboutUs) => {
    if (!window.confirm(`Delete "${item.heading}"?`)) return;
    try {
      setDeletingId(item.id);
      const result = await deleteAboutUs({ id: item.id }).unwrap();
      setSnackbar({ open: true, message: result.message || 'Deleted!', severity: 'success' });
      refetch();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error deleting.', severity: 'error' });
    } finally { setDeletingId(null); }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>About Us</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={handleAdd}
                  sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
                  Add About Us
                </Button>
              </Box>
            </Box>

            {/* Stats Cards */}
            {!isLoading && !isError && allItems.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Article      sx={{ color: '#1565c0', fontSize: 24 }} />, bg: '#e3f2fd', value: allItems.length, label: 'Total Records' },
                  { icon: <InfoOutlined sx={{ color: '#2e7d32', fontSize: 24 }} />, bg: '#e8f5e9', value: activeCount,     label: 'Active' },
                  { icon: <InfoOutlined sx={{ color: '#6d7175', fontSize: 24 }} />, bg: '#f1f2f4', value: inactiveCount,   label: 'Inactive' },
                ].map(({ icon, bg, value, label }) => (
                  <Grid item xs={12} sm={4} key={label}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Table Card */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
                  sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                  <Tab label={`All Records (${filteredItems.length})`} />
                </Tabs>
              </Box>

              {/* Search / Filter Bar */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                <TextField size="small" placeholder="Search by heading or content..." value={searchStr} onChange={handleSearch} fullWidth
                  sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Clear
                </Button>
              </Box>

              {/* Loading / Error / Empty */}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                  <CircularProgress size={32} sx={{ color: '#008060' }} />
                </Box>
              )}
              {isError && !isLoading && (
                <Box sx={{ p: 3, bgcolor: 'white' }}><Alert severity="error">Failed to load About Us records. Please try again.</Alert></Box>
              )}
              {!isLoading && !isError && paginatedList.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                  <Article sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No records found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first About Us record'}
                  </Typography>
                  {!searchStr && (
                    <Button variant="contained" startIcon={<Add />} onClick={handleAdd}
                      sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                      Add First Record
                    </Button>
                  )}
                </Box>
              )}

              {/* Table */}
              {!isLoading && paginatedList.length > 0 && (
                <Box sx={{ bgcolor: 'white' }}>
                  {/* Desktop Header */}
                  {!isMobile && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '48px 60px 1fr 1fr 80px 100px 120px', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', alignItems: 'center' }}>
                      {['', 'ID', 'Heading', 'Preview', 'Status', 'Created', 'Actions'].map(h => (
                        <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'center' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                  )}

                  {paginatedList.map((item) => {
                    const isThisDeleting = deletingId === item.id;
                    const isActive       = Number(item.is_active) === 1;

                    return (
                      <Box key={item.id}>
                        <Box
                          sx={!isMobile ? {
                            display: 'grid', gridTemplateColumns: '48px 60px 1fr 1fr 80px 100px 120px',
                            gap: 1.5, px: 2, py: 2, alignItems: 'center',
                            borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                            cursor: 'pointer',
                          } : { p: 2, borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer' }}
                          onClick={() => handleToggleExpand(item.id)}
                        >
                          {!isMobile ? (
                            <>
                              {/* Expand */}
                              <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(item.id); }} sx={{ color: '#6d7175', p: 0 }}>
                                {expandedId === item.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              {/* ID */}
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{item.id}</Typography>

                              {/* Heading */}
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{item.heading}</Typography>
                                <Typography variant="caption" color="text.secondary">{formatDateTime(item.created_at)}</Typography>
                              </Box>

                              {/* Para Preview */}
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {truncate(item.para1)}
                              </Typography>

                              {/* Status */}
                              <Chip
                                label={isActive ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                  bgcolor: isActive ? '#d1f4e0' : '#f1f2f4',
                                  color:   isActive ? '#00783e' : '#6d7175',
                                  fontWeight: 600, fontSize: '11px', height: '24px',
                                }}
                              />

                              {/* Created */}
                              <Typography variant="caption" color="text.secondary">{formatDateTime(item.created_at)}</Typography>

                              {/* Actions */}
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <IconButton onClick={e => { e.stopPropagation(); handleEdit(item); }} size="small" disabled={isThisDeleting} sx={{ color: '#202223' }}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton onClick={e => { e.stopPropagation(); handleDelete(item); }} disabled={isThisDeleting || isDeleting} size="small" sx={{ color: '#d32f2f' }}>
                                  {isThisDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            /* Mobile Card */
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223' }}>{item.heading}</Typography>
                                  <Typography variant="caption" color="text.secondary">ID: #{item.id}</Typography>
                                </Box>
                                <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(item.id); }} sx={{ color: '#6d7175' }}>
                                  {expandedId === item.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                <Chip label={isActive ? 'Active' : 'Inactive'} size="small"
                                  sx={{ bgcolor: isActive ? '#d1f4e0' : '#f1f2f4', color: isActive ? '#00783e' : '#6d7175', fontWeight: 600, fontSize: '11px', height: '22px' }} />
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded Details */}
                        <Collapse in={expandedId === item.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Record Details</Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" startIcon={<Edit />} onClick={() => handleEdit(item)} disabled={isThisDeleting}
                                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>Edit</Button>
                                <Button size="small" color="error" startIcon={isThisDeleting ? <CircularProgress size={16} /> : <Delete />}
                                  onClick={() => handleDelete(item)} disabled={isThisDeleting || isDeleting}
                                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>Delete</Button>
                              </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              {[
                                { label: 'Status',     value: isActive ? 'Active' : 'Inactive', chip: true, chipSx: isActive ? { bgcolor: '#d1f4e0', color: '#00783e' } : { bgcolor: '#f1f2f4', color: '#6d7175' } },
                                { label: 'Created At', value: formatDateTime(item.created_at),  chip: false },
                                { label: 'Updated At', value: formatDateTime(item.updated_at),  chip: false },
                              ].map(({ label, value, chip, chipSx }) => (
                                <Grid item xs={6} sm={4} key={label}>
                                  <Card sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', height: '100%' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
                                    {chip && chipSx
                                      ? <Chip label={value} size="small" sx={{ ...(chipSx as any), fontWeight: 600, fontSize: '11px', height: '22px' }} />
                                      : <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{value}</Typography>
                                    }
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Paragraphs */}
                            <Grid container spacing={2}>
                              {[
                                { label: 'Heading', value: item.heading },
                                { label: 'Paragraph 1', value: item.para1 },
                                { label: 'Paragraph 2', value: item.para2 },
                                { label: 'Paragraph 3', value: item.para3 },
                              ].filter(p => p.value).map(({ label, value }) => (
                                <Grid item xs={12} key={label}>
                                  <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>{label}</Typography>
                                    <Typography variant="body2" sx={{ color: '#202223', lineHeight: 1.7 }}>{value}</Typography>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>

            {totalPages > 1 && !isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={handleChangePage}
                  sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{editingItem ? 'Edit About Us' : 'Add About Us'}</Typography>
            <IconButton onClick={handleClose} size="small" disabled={isSubmitting}><Close /></IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>

              {/* Heading */}
              <TextField
                name="heading"
                label="Heading *"
                value={formData.heading}
                onChange={handleTextChange}
                error={!!formErrors.heading}
                helperText={formErrors.heading}
                fullWidth
                required
                disabled={isSubmitting}
                placeholder="e.g. Who We Are"
              />

              {/* Para 1 */}
              <TextField
                name="para1"
                label="Paragraph 1"
                value={formData.para1}
                onChange={handleTextChange}
                fullWidth
                multiline
                rows={3}
                disabled={isSubmitting}
                placeholder="First paragraph..."
              />

              {/* Para 2 */}
              <TextField
                name="para2"
                label="Paragraph 2"
                value={formData.para2}
                onChange={handleTextChange}
                fullWidth
                multiline
                rows={3}
                disabled={isSubmitting}
                placeholder="Second paragraph..."
              />

              {/* Para 3 */}
              <TextField
                name="para3"
                label="Paragraph 3"
                value={formData.para3}
                onChange={handleTextChange}
                fullWidth
                multiline
                rows={3}
                disabled={isSubmitting}
                placeholder="Third paragraph..."
              />

              {/* Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#fafbfb', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>Status</Typography>
                  <Typography variant="caption" color="text.secondary">Enable or disable this record</Typography>
                </Box>
                <Switch
                  checked={formData.is_active === 1}
                  onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                  disabled={isSubmitting}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#202223' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiTabs-indicator': { bgcolor: '#202223' } }}
                />
              </Box>

            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
            <Button onClick={handleClose} disabled={isSubmitting} variant="outlined"
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#f44336', color: '#fff', border: 'none', fontWeight: 500, '&:hover': { bgcolor: '#d32f2f', border: 'none' } }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained"
              disabled={isSubmitting || !formData.heading.trim()}
              sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editingItem ? 'Updating...' : 'Adding...'}
                </Box>
              ) : (editingItem ? 'Update' : 'Add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AboutUsPage;